// SAMPLE DATA — not a real ballot, not a real motion, not a real election, and
// not a real person.
//
// Four hand-written counts so the results sheet can be LOOKED AT before any of
// sign-in, persistence or a worker exists. They live in their own file, and say
// so at the top of it, precisely so nobody mistakes them for something the app
// fetched. All four are `mode: 'sandbox'`, which is also what makes the mode
// band render — a screen of tallies that is not a vote of Synod says so at the
// top and at the foot.
//
// **THESE FIXTURES ARE THE ONLY REASON THIS PAGE IS SAFE TO SERVE.** The sheet
// is Admin and Scrutineers only (registrar ruling 2026-08-16). The SERVER now
// decides the reader — `GET /api/results/:ballotId` resolves a session, an
// identity and a grant, and refuses everybody else — but this SPA has no session
// and does not call it, so what is on the screen is still these invented
// numbers, and the unauthenticated page still discloses nothing. Read the banner
// at the top of ResultPage.tsx before replacing any of this with a fetch.
//
// **The names are role-shaped on purpose.** "Candidate A (Northern)", "The Revd
// A. Mover". A plausible New Zealand Anglican name in a fixture reads as a real
// person to anyone who screenshots the page, and a screenshot of a synod result
// is the most forwardable artefact this product makes. The digests are
// obviously-invented strings rather than plausible hex for the same reason.
//
// The four exist because between them they reach every branch that matters:
//
//  1. `SAMPLE_MOTION_RESULT` — a by-Houses ballot with one motion carried and one
//     whose decision is WITHHELD (`carried: null`, a roll that contradicts the
//     papers). It is also **both halves of the Bishop rule in one sheet**: motion
//     1 is `present-and-voting`, so his paper is INSIDE the clergy figures; motion
//     2 is `summoned-per-house`, so it is excluded from every tally and reported
//     as `bishop-vote-excluded`.
//  2. `SAMPLE_ATTRIBUTABLE_RESULT` — a one-tally motion under SO 8.4. The Bishop
//     is a person present and voting, so his paper is inside the single tally,
//     and there is no scope for it to be excluded from.
//  3. `SAMPLE_ELECTION_RESULT` — an unresolved tie (SO 15.13) and a shortfall
//     caused by No Confidence (SO 15.7 / 15.12). Elections are structurally
//     always secret (`ballot_elections_are_always_secret`), so there is no
//     attributable variant of this one and there never can be.
//  4. `SAMPLE_MOTION_WITHHELD_PV_RESULT` — a by-Houses `present-and-voting`
//     motion whose clergy scope is WITHHELD because more papers were counted in
//     it (22) than there were people present to cast them (a present roll of 20):
//     `carried: null`, `votes-exceed-roll`. #1 also withholds a scope, but its
//     withheld one is `summoned-per-house`, where `rollForScope` equals the
//     summons `base` and the withheld card names the SUMMONS roll. Only here is
//     the base the VOTES (SO 8.4) and `rollForScope` the persons PRESENT, so only
//     here does the card name the PRESENT roll — the branch cca-0kwm's fix
//     reaches, and the one every ordinary motion and canon runs on.
//
// Every number below is internally consistent — the papers counted fit inside
// the members present, the marks fit inside the papers, and each `required` is
// the arithmetic the engine would have produced for that base and threshold. A
// fixture whose figures do not add up teaches the reader to stop checking.
//
// Each item result is written ONCE and referenced twice — from `result.items`
// (the count) and from `items[].result` (the words). Two copies of one tally
// would be free to disagree, and a screen showing one motion's words over
// another motion's numbers is the worst thing this package could ship.

import type { CountResult, MotionItemResult, PositionItemResult } from '@cca/decides-count';
import type { ResultView } from '../results/view';

// The engine's COUNT_ENGINE_VERSION, written as the literal rather than
// imported: this package imports the engine's TYPES only and does not bundle it
// (see README). The type is that literal, so a bump upstream reds this file —
// which is exactly what it did at **2** (registrar ruling 2026-08-16, the
// Bishop: `MotionItemResult.bishop` and `BishopTally` were removed). A fixture
// left at 1 would claim a version the engine no longer emits.
const ENGINE_VERSION = 4 as const;

const DIGESTS = {
  definitionDigest: 'sample-definition-digest-not-a-real-hash',
  resultDigest: 'sample-result-digest-not-a-real-hash',
} as const;

/* ── 1. A secret ballot of motions, counted by Houses ─────────────────────── */

const MOTION_1: MotionItemResult = {
  kind: 'motion',
  itemId: 'sample-motion-1',
  motionClass: 'ordinary-motion',
  threshold: { kind: 'simple' },
  base: 'present-and-voting',
  byHouses: true,
  scopes: [
    {
      // THE BISHOP'S PAPER IS IN THIS COLUMN, and it is invisible on purpose —
      // there is no line, no asterisk and no third column, because at engine
      // version 2 there is no separate tally of his votes to render. His aye is
      // one of the 27, and it is lawful here precisely because the base IS the
      // votes (SO 8.4): he enters numerator and denominator in the same breath.
      // Hence 43 papers against 42 clergy present in the quorum block below —
      // the 43rd is his, and he has his own roll column, not a seat in a House.
      scope: 'clergy',
      ayes: 27,
      noes: 12,
      abstentions: 3,
      blanks: 1,
      cast: 43,
      base: 39, // SO 8.4 — ayes + noes. Abstentions and blanks are not votes.
      required: 20, // floor(39 / 2) + 1
      notVoting: null, // the base IS the votes, so the subtraction is meaningless
      rollForScope: 43, // present.clergy (42) + the Bishop present (1): his paper folds into clergy on this base
      carried: true,
    },
    {
      scope: 'laity',
      ayes: 34,
      noes: 20,
      abstentions: 5,
      blanks: 2,
      cast: 61,
      base: 54,
      required: 28, // floor(54 / 2) + 1
      notVoting: null,
      rollForScope: 61, // laity present; the Bishop does not fold into the lay scope
      carried: true,
    },
  ],
  carried: true,
};

const MOTION_2: MotionItemResult = {
  kind: 'motion',
  itemId: 'sample-motion-2',
  motionClass: 'constitutional-amendment',
  threshold: { kind: 'super', numerator: 2, denominator: 3 },
  base: 'summoned-per-house',
  byHouses: true,
  scopes: [
    {
      scope: 'clergy',
      ayes: 12,
      noes: 30,
      abstentions: 0,
      blanks: 0,
      cast: 42,
      base: 45, // §15.2 — the members SUMMONED, so an absentee has cast a No
      required: 30, // ceil(2 × 45 / 3)
      notVoting: 3,
      rollForScope: 45, // a roll-derived base: rollForScope equals base (the summons roll)
      carried: false,
    },
    {
      scope: 'laity',
      ayes: 31,
      noes: 18,
      abstentions: 10,
      blanks: 2,
      cast: 61,
      base: 60,
      required: 40, // ceil(2 × 60 / 3)
      notVoting: -1, // negative: 61 papers counted against a roll of 60
      rollForScope: 60, // a roll-derived base: equals base; cast (61) > 60 is the contradiction WithheldCard renders
      // WITHHELD, not lost — and explicitly `null`, never an absent key.
      carried: null,
    },
  ],
  // The Bishop marked a paper on this motion too, and it is in NEITHER column
  // above: the base is drawn from the summons roll, which seats him on his own
  // (§15.2). It appears only as the `bishop-vote-excluded` issue below — which
  // is the whole point of that issue existing, since a silently dropped paper
  // still counts correctly and nobody ever learns it was dropped.
  //
  // Strict propagation: one withheld scope leaves the ITEM undecided, whatever
  // the other House did.
  carried: null,
};

const MOTION_COUNT: CountResult = {
  version: ENGINE_VERSION,
  ballotId: 'sample-ballot-motions',
  mode: 'sandbox',
  status: 'attention',
  fatal: [],
  issues: [
    {
      // A LAWFUL ELECTOR'S LAWFUL PAPER with no House to land in — not an
      // ineligible mark, and the copy for it says so. `error`, like its sibling
      // `ineligible-elector`: a paper was cast, it did not count, and the man
      // who cast it must be told before anything is declared.
      code: 'bishop-vote-excluded',
      severity: 'error',
      message:
        '1 paper(s) marked by the Bishop were EXCLUDED from this count and appear in no tally below. This motion is taken by Houses against a base drawn from the roll, and the roll gives the Bishop his own seat rather than a seat in a House — §15.2 / §18.1 count the members summoned in EACH House, and there are exactly two (Composition of Synod Canon 5(2), 5(3)). Counting his paper inside a House would let it supply a majority that House did not give, while his absence could never be the No the same clause makes it. Reported rather than dropped: the paper exists, it did not count, and he must be told before anything is declared',
      itemId: 'sample-motion-2',
    },
    {
      code: 'votes-exceed-roll',
      severity: 'error',
      message:
        '61 votes were cast in the laity scope against a roll of 60 — the roll snapshot and the vote set disagree, so no decision is reported for this scope (carried: null) until a human says which of the two is wrong',
      itemId: 'sample-motion-2',
    },
    {
      // Raised once for the BALLOT, with no itemId: a short summons list is a
      // fact about the roll, equally true of every motion on the paper.
      code: 'present-exceeds-summons',
      severity: 'error',
      message:
        'roll.present.laity is 61 but only 60 were summoned — more people in the room than on the summons list is the signature of a short summons fetch, and that list is both the quorum denominator (SO 12.1) and the §15.2 / §18.1 base, so a short one lowers the bar for amending the Constitution. Reported and counted, never refused: cl 9(2) contemplates a person voting who was not summoned, so this needs a human before the declaration',
    },
  ],
  quorum: {
    houses: [
      { house: 'clergy', summoned: 45, present: 42, required: 23, met: true },
      { house: 'laity', summoned: 60, present: 61, required: 30, met: true },
    ],
    met: true,
  },
  items: [MOTION_1, MOTION_2],
};

export const SAMPLE_MOTION_RESULT: ResultView = {
  // STATED BY THE FIXTURE, because `viewer` is required and nothing defaults it
  // any more (see ResultView.viewer). A preview has no server to ask, so it says
  // which reader it is a preview OF — and it says the LEAST privileged of the
  // two, since the Admin's block is the one thing the field decides.
  viewer: 'scrutineer',
  ballot: {
    title: 'Sample sitting — motions',
    state: 'closed',
    // A motion ballot MAY be secret (0001_initial.sql:233). Nothing on the
    // sheet renders this now — see BallotSecrecy — but the fixture carries the
    // ballot's real state rather than dropping a field because no component
    // currently reads it.
    secrecy: 'secret',
  },
  result: MOTION_COUNT,
  items: [
    {
      kind: 'motion',
      title: 'Motion 1 — That the report of the Standing Committee be received',
      instrument:
        'That the report of the Standing Committee for the year ending 31 December be received, ' +
        'and that the thanks of Synod be recorded to those who served on it.',
      mover: 'The Revd A. Mover',
      seconder: 'Mr B. Seconder',
      result: MOTION_1,
    },
    {
      kind: 'motion',
      title: 'Motion 2 — Amendment to Clause 15 of the Constitution',
      instrument:
        'That Clause 15 of the Constitution be amended by deleting from sub-clause 15.1 the ' +
        'words “twenty-one days” and inserting in their place the words “twenty-eight days”, so ' +
        'that the notice required before an amendment to the Constitution may be considered by ' +
        'Synod is lengthened accordingly; and that the amendment take effect from the close of ' +
        'this session.',
      mover: 'The Revd C. Mover',
      seconder: 'Mrs D. Seconder',
      result: MOTION_2,
    },
  ],
  provenance: {
    countRunId: 'sample-count-run-motions',
    engineVersion: ENGINE_VERSION,
    ...DIGESTS,
    runAtNz: '15 August 2026, 8:42pm (NZ)',
    rollDigest: 'sample-roll-digest-not-a-real-hash',
    rollAmendmentSeq: 0,
  },
  // The box, the roll, the window. Summoned figures match the quorum block
  // above (45 clergy, 60 laity, the Bishop); 104 papers is every paper motion 1
  // was marked on (43 in the clergy scope + 61 lay). Ran to its published close.
  coverage: {
    papersSealedCount: 104,
    papersSealedAtNz: '15 August 2026, 8:31pm (NZ)',
    summonedClergy: 45,
    summonedLaity: 60,
    summonedBishop: 1,
    closesAtNz: '8:15pm on 15 August 2026 (NZ)',
    extensions: [],
  },
  // STATED, because the field is required and nothing defaults it (see
  // ResultView.rollIntegrity). `clean` draws nothing, so all four previews look
  // exactly as they did before the check reached the sheet.
  rollIntegrity: { state: 'clean' },
};

/* ── 2. An attributable motion, counted as one tally (SO 8.4) ─────────────── */

const MOTION_3: MotionItemResult = {
  kind: 'motion',
  itemId: 'sample-motion-3',
  motionClass: 'ordinary-motion',
  threshold: { kind: 'simple' },
  base: 'present-and-voting',
  byHouses: false,
  scopes: [
    {
      scope: 'all',
      ayes: 55,
      noes: 30,
      abstentions: 4,
      blanks: 1,
      cast: 90,
      base: 85,
      required: 43, // floor(85 / 2) + 1
      notVoting: null,
      rollForScope: 101, // clergy 42 + laity 58 + the Bishop present (1); the one-tally base holds him
      carried: true,
    },
  ],
  // A one-tally count under SO 8.4 is of "the persons present and voting", which
  // the Bishop is — so his aye is inside the 55 above, and there is nothing to
  // say about it. `all` contains him under every one of the three bases (§9.7 /
  // §19.1 make him a member of Synod), so this is the one shape where his paper
  // can never be excluded.
  carried: true,
};

const ONE_TALLY_COUNT: CountResult = {
  version: ENGINE_VERSION,
  ballotId: 'sample-ballot-one-tally',
  mode: 'sandbox',
  status: 'ok',
  fatal: [],
  issues: [],
  quorum: {
    houses: [
      { house: 'clergy', summoned: 45, present: 42, required: 23, met: true },
      { house: 'laity', summoned: 60, present: 58, required: 30, met: true },
    ],
    met: true,
  },
  items: [MOTION_3],
};

export const SAMPLE_ATTRIBUTABLE_RESULT: ResultView = {
  viewer: 'scrutineer',
  ballot: {
    title: 'Sample sitting — motion taken as one tally',
    state: 'closed',
    // The other value of the field, kept so both are exercised by a fixture.
    secrecy: 'attributable',
  },
  result: ONE_TALLY_COUNT,
  items: [
    {
      kind: 'motion',
      title: 'Motion 3 — That the hour of adjournment be extended',
      instrument:
        'That the hour of adjournment be extended by one hour, so that the remaining business ' +
        'on the agenda paper may be concluded in this session.',
      mover: 'The Revd E. Mover',
      seconder: 'Mr F. Seconder',
      result: MOTION_3,
    },
  ],
  provenance: {
    countRunId: 'sample-count-run-one-tally',
    engineVersion: ENGINE_VERSION,
    ...DIGESTS,
    runAtNz: '15 August 2026, 9:05pm (NZ)',
    rollDigest: 'sample-roll-digest-not-a-real-hash',
    rollAmendmentSeq: 0,
  },
  // THE EXTENDED-POLL BRANCH lives on this sheet — fitting, for a motion about
  // extending the hour of adjournment. The close shown is the one that finally
  // stood (8:55pm); the moved statement and the chain's stored sentence say it
  // was not the close the electors were first told, which is exactly what a
  // signed sheet must not hide. The reason is the extend route's own shape,
  // `close extended from <old> to <new>`, carried verbatim and never parsed.
  coverage: {
    papersSealedCount: 90,
    papersSealedAtNz: '15 August 2026, 8:58pm (NZ)',
    summonedClergy: 45,
    summonedLaity: 60,
    summonedBishop: 1,
    closesAtNz: '8:55pm on 15 August 2026 (NZ)',
    extensions: [
      {
        occurredAtNz: '15 August 2026, 8:27pm (NZ)',
        reason: 'close extended from 2026-08-15T20:30 to 2026-08-15T20:55',
      },
    ],
  },
  rollIntegrity: { state: 'clean' },
};

/* ── 3. A secret ballot of elections ──────────────────────────────────────── */

const POSITION_1: PositionItemResult = {
  kind: 'position',
  itemId: 'sample-position-1',
  method: 'fptp',
  electorate: 'clergy',
  seats: 1,
  alternateSeats: 1,
  positions: 2,
  outcome: 'tie-unresolved',
  totals: [
    { candidateId: 'sample-candidate-a', votes: 20 },
    { candidateId: 'sample-candidate-b', votes: 14 },
    { candidateId: 'sample-candidate-c', votes: 14 },
    { candidateId: 'sample-candidate-d', votes: 6 },
  ],
  noConfidence: 2,
  papers: {
    counted: 30,
    abstain: 2,
    blank: 1,
    noConfidence: 2,
    overVote: 0,
    duplicateMarks: 1,
    ineligible: 0,
  },
  rounds: [
    {
      round: 1,
      totals: [
        { candidateId: 'sample-candidate-a', votes: 20 },
        { candidateId: 'sample-candidate-b', votes: 14 },
        { candidateId: 'sample-candidate-c', votes: 14 },
        { candidateId: 'sample-candidate-d', votes: 6 },
      ],
      noConfidence: 2,
    },
  ],
  // The walk took A, then stopped: two candidates level on 14 with one place
  // left is a tie that MATTERS, and the engine reports it rather than breaking
  // it. The place stays unfilled until SO 15.13 has run — it does NOT go to
  // Standing Committee on those grounds.
  elected: ['sample-candidate-a'],
  substantive: ['sample-candidate-a'],
  alternates: [],
  unfilled: 1,
  referToStandingCommittee: true,
  ties: [
    {
      at: 'election',
      contenders: [
        { kind: 'candidate', candidateId: 'sample-candidate-b' },
        { kind: 'candidate', candidateId: 'sample-candidate-c' },
      ],
      votes: 14,
      seatsAvailable: 1,
      resolved: false,
    },
  ],
};

const POSITION_2: PositionItemResult = {
  kind: 'position',
  itemId: 'sample-position-2',
  method: 'fptp',
  electorate: 'laity',
  seats: 5,
  alternateSeats: 0,
  positions: 5,
  outcome: 'shortfall',
  totals: [
    { candidateId: 'sample-candidate-e', votes: 42 },
    { candidateId: 'sample-candidate-f', votes: 38 },
    { candidateId: 'sample-candidate-g', votes: 33 },
    { candidateId: 'sample-candidate-h', votes: 22 },
    { candidateId: 'sample-candidate-i', votes: 15 },
  ],
  // 30 papers marked No Confidence, which ranks it FOURTH — above H and I.
  // Everything below it is refused, so two places stay unfilled and the
  // runner-up is not elected.
  noConfidence: 30,
  papers: {
    counted: 40,
    abstain: 3,
    blank: 2,
    noConfidence: 30,
    overVote: 1,
    duplicateMarks: 0,
    ineligible: 4,
  },
  rounds: [
    {
      round: 1,
      totals: [
        { candidateId: 'sample-candidate-e', votes: 42 },
        { candidateId: 'sample-candidate-f', votes: 38 },
        { candidateId: 'sample-candidate-g', votes: 33 },
        { candidateId: 'sample-candidate-h', votes: 22 },
        { candidateId: 'sample-candidate-i', votes: 15 },
      ],
      noConfidence: 30,
    },
  ],
  elected: ['sample-candidate-e', 'sample-candidate-f', 'sample-candidate-g'],
  substantive: ['sample-candidate-e', 'sample-candidate-f', 'sample-candidate-g'],
  alternates: [],
  unfilled: 2,
  referToStandingCommittee: true,
  ties: [],
};

const ELECTION_COUNT: CountResult = {
  version: ENGINE_VERSION,
  ballotId: 'sample-ballot-elections',
  mode: 'sandbox',
  status: 'attention',
  fatal: [],
  issues: [
    {
      code: 'tie-unresolved',
      severity: 'error',
      message:
        'a tie at a cut line has no recorded resolution — SO 15.13 requires a fresh vote among the tied, then a draw by the Registrar, before anything may be declared',
      itemId: 'sample-position-1',
    },
    {
      code: 'duplicate-mark',
      severity: 'warning',
      message:
        '1 paper marked the same candidate twice — the repeat was counted once and the paper was not rejected',
      itemId: 'sample-position-1',
    },
    {
      code: 'ineligible-elector',
      severity: 'error',
      message:
        '4 paper(s) came from electors who may not mark a laity position (SO 15.8) and were excluded',
      itemId: 'sample-position-2',
    },
    {
      code: 'over-vote',
      severity: 'warning',
      message: '1 paper marked more candidates than there are places and was not counted',
      itemId: 'sample-position-2',
    },
    {
      code: 'no-confidence-carried',
      severity: 'warning',
      message:
        'No Confidence outranked one or more candidates — those places are UNFILLED and go to Standing Committee (SO 15.7, SO 15.12). The runner-up is not elected',
      itemId: 'sample-position-2',
    },
  ],
  quorum: {
    houses: [
      { house: 'clergy', summoned: 45, present: 43, required: 23, met: true },
      { house: 'laity', summoned: 90, present: 85, required: 45, met: true },
    ],
    met: true,
  },
  items: [POSITION_1, POSITION_2],
};

export const SAMPLE_ELECTION_RESULT: ResultView = {
  viewer: 'scrutineer',
  ballot: {
    title: 'Sample sitting — elections',
    state: 'closed',
    // Not a choice: SO 15.9 is structural in the schema, so an election ballot
    // cannot exist as anything but secret.
    secrecy: 'secret',
  },
  result: ELECTION_COUNT,
  items: [
    {
      kind: 'position',
      title: 'Church Tribunal — one clergy member and one alternate',
      candidateNames: {
        'sample-candidate-a': 'Candidate A (Northern)',
        'sample-candidate-b': 'Candidate B (Central)',
        'sample-candidate-c': 'Candidate C (Southern)',
        'sample-candidate-d': 'Candidate D (Northern)',
      },
      result: POSITION_1,
    },
    {
      kind: 'position',
      title: 'Standing Committee — five lay members',
      candidateNames: {
        'sample-candidate-e': 'Candidate E (Northern)',
        'sample-candidate-f': 'Candidate F (Central)',
        'sample-candidate-g': 'Candidate G (Southern)',
        'sample-candidate-h': 'Candidate H (Central)',
        'sample-candidate-i': 'Candidate I (Southern)',
      },
      result: POSITION_2,
    },
  ],
  provenance: {
    countRunId: 'sample-count-run-elections',
    engineVersion: ENGINE_VERSION,
    ...DIGESTS,
    runAtNz: '15 August 2026, 9:31pm (NZ)',
    rollDigest: 'sample-roll-digest-not-a-real-hash',
    rollAmendmentSeq: 0,
  },
  // Summoned figures match the quorum block above (45 clergy, 90 laity, the
  // Bishop). 79 papers: 33 clergy (P1's 30 counted + 2 abstain + 1 blank) and 46
  // lay (P2's 40 + 3 + 2 + 1 over-vote); P2's 4 ineligible marks ride clergy
  // papers already inside the 33, so they add no paper to the box. Ran to close.
  coverage: {
    papersSealedCount: 79,
    papersSealedAtNz: '15 August 2026, 9:24pm (NZ)',
    summonedClergy: 45,
    summonedLaity: 90,
    summonedBishop: 1,
    closesAtNz: '9:10pm on 15 August 2026 (NZ)',
    extensions: [],
  },
  rollIntegrity: { state: 'clean' },
};

/* ── 4. A present-and-voting motion with a scope WITHHELD (cca-0kwm) ───────── */

// The branch the first three sheets never reach. The only withheld scope among
// them is #1's laity column, and that motion is `summoned-per-house`: there
// `rollForScope` equals the summons `base` (60 == 60), so the withheld card
// names the SUMMONS roll and no present head-count ever appears. Every ordinary
// motion and every canon Synod passes is `present-and-voting` instead, where the
// base is the VOTES (SO 8.4) and `rollForScope` is a different number — the
// persons PRESENT — so `cast > rollForScope` is the contradiction and the card
// has to name the PRESENT roll. This sheet is the one that makes it do so, and
// before cca-0kwm the scope carried no `rollForScope` for it to read.
//
// A by-Houses division: the clergy count is contradicted and WITHHELD, the laity
// carried. One withheld scope leaves the whole item undecided (strict
// propagation, motion.ts), so the item is `carried: null` and the laity's
// "Carried" sits in the tally beneath a verdict that reports no decision.

const MOTION_PV_WITHHELD: MotionItemResult = {
  kind: 'motion',
  itemId: 'sample-motion-pv-withheld',
  motionClass: 'ordinary-motion',
  threshold: { kind: 'simple' },
  base: 'present-and-voting',
  byHouses: true,
  scopes: [
    {
      // MORE PAPERS THAN PEOPLE — the branch cca-0kwm's fix renders. 22 papers
      // were counted in the clergy scope against 20 people present to cast one:
      // 19 clergy and the Bishop, who is a clergy elector on a present-and-voting
      // base (the base is the votes, so he enters numerator and denominator
      // together; `bishopBelongsIn`, motion.ts). His present head joins this
      // scope's roll even though the roll seats him on his own, so `rollForScope`
      // = present.clergy (19) + the Bishop present (1) = 20 — the bishop-aware
      // present head-count. `cast` (22) exceeds it by 2: the roll and the votes
      // disagree, and the decision is WITHHELD.
      scope: 'clergy',
      ayes: 13,
      noes: 6,
      abstentions: 2,
      blanks: 1,
      cast: 22,
      base: 19, // SO 8.4 — ayes + noes. Abstentions and blanks are not votes.
      required: 10, // floor(19 / 2) + 1
      notVoting: null, // the base IS the votes, so the subtraction is meaningless
      rollForScope: 20, // present.clergy (19) + the Bishop present (1); cast (22) > 20 is the contradiction
      // WITHHELD, not lost — and explicitly `null`, never an absent key.
      carried: null,
    },
    {
      // The laity carried cleanly: 44 papers against 46 lay members present, so
      // there are fewer papers than people (two present lay members handed in no
      // paper) and nothing here is contradicted. The Bishop does NOT fold into
      // the lay scope — his fold is with the clergy — so `rollForScope` is the
      // bare lay present head-count.
      scope: 'laity',
      ayes: 30,
      noes: 10,
      abstentions: 3,
      blanks: 1,
      cast: 44,
      base: 40, // ayes + noes
      required: 21, // floor(40 / 2) + 1
      notVoting: null,
      rollForScope: 46, // laity present; the Bishop does not fold into the lay scope
      carried: true, // base > 0 and ayes (30) >= required (21)
    },
  ],
  // Strict propagation: one withheld scope leaves the ITEM undecided, whatever
  // the other House did.
  carried: null,
};

const MOTION_PV_WITHHELD_COUNT: CountResult = {
  version: ENGINE_VERSION,
  ballotId: 'sample-ballot-motion-withheld-pv',
  mode: 'sandbox',
  status: 'attention',
  fatal: [],
  issues: [
    {
      // The one finding on this sheet, and the reason the clergy decision is
      // withheld. Worded EXACTLY as motion.ts builds it — `cast`, then the raw
      // scope name, then `rollForScope` — so the fixture reads as something the
      // engine emitted rather than something a hand invented.
      code: 'votes-exceed-roll',
      severity: 'error',
      message:
        '22 votes were cast in the clergy scope against a roll of 20 — the roll snapshot and the vote set disagree, so no decision is reported for this scope (carried: null) until a human says which of the two is wrong',
      itemId: 'sample-motion-pv-withheld',
    },
  ],
  quorum: {
    houses: [
      // Quorum EXCLUDES the Bishop — SO 12.1 measures presence in a House and he
      // sits in neither (RollCounts.bishop). So present.clergy here is 19, one
      // fewer than the clergy `rollForScope` of 20 above, and the difference is
      // exactly his folded head. Present is within summons in both Houses, so
      // there is no `present-exceeds-summons` alongside the withheld scope.
      { house: 'clergy', summoned: 34, present: 19, required: 17, met: true },
      { house: 'laity', summoned: 60, present: 46, required: 30, met: true },
    ],
    met: true,
  },
  items: [MOTION_PV_WITHHELD],
};

export const SAMPLE_MOTION_WITHHELD_PV_RESULT: ResultView = {
  viewer: 'scrutineer',
  ballot: {
    title: 'Sample sitting — a withheld present-and-voting motion',
    state: 'closed',
    // A motion ballot MAY be secret (0001_initial.sql:233); this one is. On a
    // present-and-voting base the Bishop's paper is counted with the clergy
    // whether it is stamped `bishop` (attributable) or `clergy` (secret, trigger
    // 15), so the tally is the same either way — the field carries the ballot's
    // real state, not a value a component reads.
    secrecy: 'secret',
  },
  result: MOTION_PV_WITHHELD_COUNT,
  items: [
    {
      kind: 'motion',
      title: 'Motion 4 — That the revised safe-ministry guidelines be adopted',
      instrument:
        'That the revised guidelines for safe ministry in parishes, as circulated with the agenda ' +
        'paper, be adopted by Synod and commended to every parish for use from the beginning of the ' +
        'next calendar year.',
      mover: 'The Revd G. Mover',
      seconder: 'Mrs H. Seconder',
      result: MOTION_PV_WITHHELD,
    },
  ],
  provenance: {
    countRunId: 'sample-count-run-motion-withheld-pv',
    engineVersion: ENGINE_VERSION,
    ...DIGESTS,
    runAtNz: '15 August 2026, 9:52pm (NZ)',
    rollDigest: 'sample-roll-digest-not-a-real-hash',
    rollAmendmentSeq: 0,
  },
  // THE MANUAL-WINDOW BRANCH lives on this sheet: no closing time was ever
  // published, so the window sentence says the poll stayed open until a
  // Scrutineer closed it — no "ran to its published close" tail, because there
  // was none to run to. Summoned figures match the quorum block (34/60/1); 66
  // papers is the two scopes' casts (22 + 44), the clergy 22 being the very
  // contradiction this sheet exists to render.
  coverage: {
    papersSealedCount: 66,
    papersSealedAtNz: '15 August 2026, 9:47pm (NZ)',
    summonedClergy: 34,
    summonedLaity: 60,
    summonedBishop: 1,
    closesAtNz: null,
    extensions: [],
  },
  rollIntegrity: { state: 'clean' },
};

/** The four sheets, keyed by the fragment the shell reads. Each one STATES
 *  `viewer: 'scrutineer'` — the render site no longer defaults it, because a
 *  default is the browser deciding the reader by omission — and none previews
 *  a member reading, because there is no member reading of this page at all
 *  (registrar ruling 2026-08-16). */
export const SAMPLE_RESULTS: Record<string, ResultView> = {
  motions: SAMPLE_MOTION_RESULT,
  'one-tally': SAMPLE_ATTRIBUTABLE_RESULT,
  elections: SAMPLE_ELECTION_RESULT,
  'motion-withheld': SAMPLE_MOTION_WITHHELD_PV_RESULT,
};
